/**
 * Enclosed interior faces of a wall sketch — the spaces its walls bound,
 * independent of any room-measure polygons. Computed geometrically: a padded
 * bounding rectangle minus the wall bodies (Manifold 2D boolean) yields one
 * face per enclosed space plus the unbounded outside, which is dropped by
 * testing a bounding-box corner. Used by the Wall-sketch representation for
 * room-tag hovering, label areas, and positional filing.
 */

import { FootprintPolygon, polygonMinusRegions } from './manifoldCsg';
import type { PointoramaWallSketch } from './PointoramaWallSketch';

/** Point-in-ring for [x, y] tuple rings. */
export function pointInRing(x: number, y: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Absolute area of a [x, y] tuple ring. */
export function ringArea(ring: [number, number][]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s / 2);
}

/** Face area: outer minus holes. */
export function faceArea(face: FootprintPolygon): number {
  let area = ringArea(face.outer);
  for (const hole of face.holes) area -= ringArea(hole);
  return area;
}

/**
 * The wall sketch's enclosed interior faces. Returns null while the Manifold
 * WASM module isn't loaded; an empty array when the sketch has no walls.
 */
export function computeEnclosedFaces(sketch: PointoramaWallSketch): FootprintPolygon[] | null {
  const rings = sketch.getWallRegionRings();
  if (rings.length === 0) return [];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of rings) {
    for (const p of ring) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  const margin = 1;
  const rect = [
    { x: minX - margin, y: minY - margin },
    { x: maxX + margin, y: minY - margin },
    { x: maxX + margin, y: maxY + margin },
    { x: minX - margin, y: maxY + margin },
  ];

  const faces = polygonMinusRegions(rect, rings);
  if (!faces) return null;

  // Drop the unbounded outside: the face containing a bbox corner (enclosed
  // faces can't reach past the walls into the padding).
  const cornerX = minX - margin + 1e-4;
  const cornerY = minY - margin + 1e-4;
  return faces.filter((face) => !pointInRing(cornerX, cornerY, face.outer));
}

/** The face containing the point (inside the outer ring, outside every hole). */
export function faceContainingPoint(faces: readonly FootprintPolygon[], point: { x: number; y: number }): FootprintPolygon | null {
  for (const face of faces) {
    if (!pointInRing(point.x, point.y, face.outer)) continue;
    if (face.holes.some((hole) => pointInRing(point.x, point.y, hole))) continue;
    return face;
  }
  return null;
}
